import { round } from "src/utils"
import { clearBasketLocal, saveBasketLocal } from "src/utils/localstorage"
import {
    buildCatalog,
    isMenuUsable,
    reconcileBasket,
} from "src/utils/reconcileBasket"
import { fetchMenu } from "src/redux/menu/actions"
import { fetchCarousel } from "src/redux/carousel"

const ADD_ITEM = "basket/ADD_ITEM"
const CLEAR_BASKET = "basket/CLEAR_BASKET"
const DELETE_ITEM = "basket/DELETE_ITEM"
const SET_ITEMS = "basket/SET_ITEMS"
const INCREMENT_QUANTITY = "basket/INCREMENT_QUANTITY"
const DECREMENT_QUANTITY = "basket/DECREMENT_QUANTITY"
const SYNC_WITH_MENU = "basket/SYNC_WITH_MENU"
const MENU_SYNC_FAILED = "basket/MENU_SYNC_FAILED"

// pricesVerified: the item prices come from a successfully loaded menu (an
//   empty basket has nothing to verify). Prices restored from localStorage are
//   unverified until the basket is reconciled with the menu (SUSHI-435).
// menuError: the last attempt to load the menu for reconciliation failed.
// Prices and the total may be shown only when pricesVerified; checkout needs
// pricesVerified && !menuError.
const initState = {
    items: [],
    totalPrice: 0,
    pricesVerified: true,
    menuError: false,
}

export const clearBasket = () => ({
    type: CLEAR_BASKET,
})

export default function(state = initState, action) {
    const { type, payload } = action

    const cases = {
        [CLEAR_BASKET]: () => {
            clearBasketLocal()

            return {
                ...state,
                items: [],
                totalPrice: 0,
                pricesVerified: true,
            }
        },

        [SET_ITEMS]: () => {
            const items = Array.isArray(payload) ? payload : []
            const totalPrice = getTotalPrice(items)
            return {
                ...state,
                items,
                totalPrice,
                pricesVerified: items.length === 0,
            }
        },
        [SYNC_WITH_MENU]: () => {
            const { items } = payload
            const totalPrice = getTotalPrice(items)
            saveBasketLocal(items)
            return {
                ...state,
                items,
                totalPrice,
                pricesVerified: true,
                menuError: false,
            }
        },
        [MENU_SYNC_FAILED]: () => ({
            ...state,
            menuError: true,
        }),
        [ADD_ITEM]: () => {
            const items = addItems(state.items, payload)
            const totalPrice = getTotalPrice(items)
            saveBasketLocal(items)
            return {
                ...state,
                items,
                totalPrice,
            }
        },

        [DELETE_ITEM]: () => {
            const items = removeItem(state.items, payload)
            const totalPrice = getTotalPrice(items)
            saveBasketLocal(items)
            return {
                ...state,
                items,
                totalPrice,
                pricesVerified: items.length === 0 || state.pricesVerified,
            }
        },
        [INCREMENT_QUANTITY]: () => {
            const items = incrementQty(state.items, payload)
            const totalPrice = getTotalPrice(items)
            saveBasketLocal(items)
            return {
                ...state,
                items,
                totalPrice,
            }
        },
        [DECREMENT_QUANTITY]: () => {
            const items = decrementQty(state.items, payload)
            const totalPrice = getTotalPrice(items)
            saveBasketLocal(items)
            return {
                ...state,
                items,
                totalPrice,
            }
        },
    }

    return cases[type] ? cases[type]() : state
}

//Actions
export const addItem = item => ({ type: ADD_ITEM, payload: item })
export const deleteItem = id => ({ type: DELETE_ITEM, payload: id })
export const setItems = items => ({ type: SET_ITEMS, payload: items })
export const incrementItemQty = id => ({
    type: INCREMENT_QUANTITY,
    payload: id,
})
export const decrementItemQty = id => ({
    type: DECREMENT_QUANTITY,
    payload: id,
})
export const syncWithMenu = reconciliation => ({
    type: SYNC_WITH_MENU,
    payload: reconciliation,
})
export const menuSyncFailed = () => ({ type: MENU_SYNC_FAILED })

/**
 * Loads the menu and the carousel offer (fetchMenu / fetchCarousel;
 * forceRefresh bypasses their caches) and reconciles the basket with them:
 * current prices, items no longer sold removed silently. Never reconciles
 * against an empty or failed menu — the basket is then left untouched and
 * menuError is set.
 *
 * Both requests start together, but the carousel is waited for only when the
 * menu lists alone do not sell every basket item. The menu lists win over the
 * carousel anyway (see buildCatalog), so a slow carousel request never delays
 * a basket that the menu covers.
 *
 * The carousel takes part only as the list that fetchCarousel has just loaded
 * (from Shop-api or its 30-minute cache) and returned: the copy restored from
 * localStorage (CAROUSEL_LIST) never expires, so it may still list deleted
 * products at old prices. A failed carousel alone is not a menu failure — the
 * basket is then checked against the menu lists only.
 *
 * Resolves to null when the menu could not be loaded, otherwise to
 * { items, totalPrice, changed } of the reconciled basket.
 */
export const syncBasketWithMenu = (forceRefresh = false) => async (
    dispatch,
    getState
) => {
    const menuRequest = dispatch(fetchMenu(forceRefresh))
    // Never rejects (fetchCarousel resolves to null on failure).
    const carouselRequest = dispatch(fetchCarousel(forceRefresh))

    const isMenuLoaded = await menuRequest
    const { menu } = getState()
    if (!isMenuLoaded || !isMenuUsable(menu)) {
        dispatch(menuSyncFailed())
        return null
    }

    // Only what the shop offers: the raw Shop-api lists also hold products
    // hidden from the shop (isVisible: false, mostly priced 0) and raw
    // ingredients, which must not survive in a saved basket.
    const menuLists = {
        sets: menu.sets,
        products: menu.filteredProducts,
        ingredients: menu.filteredIngredients,
    }
    let catalog = buildCatalog(menuLists)
    if (reconcileBasket(getState().basket.items, catalog).removed.length > 0) {
        const freshCarousel = await carouselRequest
        catalog = buildCatalog({ ...menuLists, carousel: freshCarousel })
    }

    // The basket is read after the last await, so a change made meanwhile
    // (e.g. an item added) is reconciled too, not overwritten.
    const { items, changed } = reconcileBasket(getState().basket.items, catalog)
    dispatch(syncWithMenu({ items, changed }))

    return { items, totalPrice: getTotalPrice(items), changed }
}

// helper functions
function addItems(items, newItem) {
    const index = items.findIndex(item => item.id === newItem.id)
    if (index === -1) items.push(newItem)
    else {
        items[index].quantity += newItem.quantity
    }
    return items
}
function incrementQty(items, id) {
    return items.filter(set => {
        if (set.id === id) {
            set.quantity = set.quantity + 1
            return set
        }
        return set
    })
}
function decrementQty(items, id) {
    return items.filter(set => {
        if (set.id === id && set.quantity > 1) {
            set.quantity = set.quantity - 1
            return set
        }
        return set
    })
}
function removeItem(items, id) {
    return items.filter(set => set.id !== id)
}

function getTotalPrice(items) {
    return round(
        items.reduce(
            (totalPrice, item) =>
                (totalPrice += parseFloat(item.price * item.quantity)),
            0
        ),
        2
    )
}
